import React from "react";
import { Helmet } from "react-helmet";
import { CgInternal } from "react-icons/cg";
import { CiLocationOn } from "react-icons/ci";
import { FaBarcode } from "react-icons/fa";
import { GiGraduateCap } from "react-icons/gi";
import { GoDot, GoLink } from "react-icons/go";
import { HiOutlinePhone } from "react-icons/hi2";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import { IoCodeSlashOutline } from "react-icons/io5";
import { MdOutlineMail } from "react-icons/md";
import { RiExternalLinkLine } from "react-icons/ri";

import { Link } from "react-router-dom";
import { database } from "../config";
import { useAppContext } from "../context/ThemeContext";
import AIComponent from "../reusable-components/AIComponent";
import { Achievements } from "../reusable-components/Achievements";
import { FloatLinks } from "../reusable-components/FloatLinks";
import Layout from "../reusable-components/Layout";
import { MicroFrontEnd } from "../reusable-components/MicroFrontEnd";
import { Spiral } from "../reusable-components/Spiral";
import { TechStackGroup } from "../reusable-components/TechStackGroup";
import { TechStackLeftSummary } from "../reusable-components/TechStackLeftSummary";
import { TechStackRightSummary } from "../reusable-components/TechStackRightSummary";
import Accordion from "../reusable-components/custom-component/Accordion";
import WordRotator from "../reusable-components/custom-component/WordRotator";

function Profile() {
  const { siteTheme, state } = useAppContext();
  const [techStacks, setTechStacks] = React.useState([]);
  const [achievements, setAchievements] = React.useState([]);
  const [aiData, setAi] = React.useState([]);
  const [education, setEducation] = React.useState([]);
  const [school, setSchool] = React.useState([]);
  const [experience, setExperience] = React.useState([]);
  const [profileSummaries, setProfileSummaries] = React.useState({});
  const [mfe, setMfe] = React.useState({});
  const [profileName, setProfileName] = React.useState("");
  const [profile, setProfile] = React.useState({});
  const [textWrap, setTextWrap] = React.useState([]);
  const [otherInfo, setOtherInfo] = React.useState("");

  let isDay = state.isDay;
  if (JSON.parse(sessionStorage.getItem("isDay"))) {
    isDay = JSON.parse(sessionStorage.getItem("isDay"));
  }

  React.useEffect(() => {
    const unsubscribe = database
      .collection("tech-stacks")
      .onSnapshot((snapshot) => {
        snapshot.docs.forEach((doc) => {
          if (doc.data().slug) {
            setProfileSummaries(doc.data().profile);
            setTechStacks(doc.data().stackData);
            setProfileName(doc.data().name);
            setAchievements(doc.data().achievements);
            setAi(doc.data().ai);
            setOtherInfo(doc.data().props);
            setMfe(doc.data().mfe);
            setProfile(doc.data().profile.job);
            setTextWrap(doc.data().textWrap);
            setEducation(doc.data().education);
            setSchool(doc.data().schools);
            setExperience(doc.data().experience);
          }
        });
      });
    return () => unsubscribe();
  }, []);

  const aiTechStacks = aiData?.tools?.map((tool) => {
    return {
      index: tool?.index,
      value: {
        src: tool?.link,
        name: tool?.name,
      },
    };
  });

  return (
    <>
      <Helmet>
        <title>Portfolio | {profileName}</title>
      </Helmet>
      <Layout profileName={profileName}>
        <div className="relative w-full h-full overflow-x-hidden">
          <div
            className={`${
              isDay ? "bg-[rgba(0,0,0,0.05)]" : "bg-[rgba(0,0,0,0.3)]"
            } relative w-auto h-[450px] -ml-[40em] -mr-[40em] rounded-br-[50%] rounded-bl-[50%]`}
          >
            <div className="relative flex w-[30%] md:w-[40%] lg:w-[45%] xl:w-full px-4 pt-32 pb-20 mx-auto max-w-max-profile-content-width md:justify-center">
              <div className="relative flex items-center justify-end w-full h-[200px] ">
                <div className="absolute bottom-0 left-0 right-0 z-20">
                  <span
                    className={`${siteTheme.textColor} duration-300 ease-in-out absolute top-[18%] left-20 text-2xl tracking-[0.5em] font-thin drop-shadow-[0_0_5px_#ffea00]`}
                  >
                    ...think
                  </span>
                  <p className="text-[240px] text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 to-pink-500 h-[180px] leading-none opacity-[0.3] font-thin drop-shadow-[0_0_10px_#ffea00]">
                    innovate
                  </p>
                </div>
                <Spiral siteTheme={siteTheme} />
              </div>
            </div>
          </div>
          <div
            className={`${
              isDay ? "border-neutral-400" : "border-neutral-800"
            } border-b-[.5px] flex flex-col w-full gap-20 m-2 pb-16 mx-auto lg:flex-row max-w-max-profile-content-width px-2 md:px-5 lg:px-0`}
          >
            <div
              className={`${
                isDay ? siteTheme.textColor : "text-neutral-400"
              } gap-5 flex flex-col duration-300 ease-in-out`}
            >
              <div className="flex flex-row items-end w-full gap-5">
                <FaBarcode size={55} color="rgba(117, 117, 117, 0.2)" />
                <div className="w-full">
                  <p className={`font-saira text-2xl`}>{profileName}</p>
                  <div className={`font-saira`}>
                    <WordRotator
                      words={textWrap}
                      wrapperstyles={`h-[1rem] lg:w-full`}
                      textstyles="font-thin text-xs tracking-widest"
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-10 sm:gap-20 sm:flex-row sm:items-start">
                <div className="flex flex-col gap-[5px] font-saira">
                  <div className="flex flex-row items-start gap-3 text-sm font-thin">
                    <IoCodeSlashOutline size={16} />
                    <p>{profile?.title}</p>
                  </div>
                  <div className="flex flex-row items-start gap-3 text-sm font-thin">
                    <IoCodeSlashOutline size={16} />
                    <p>{profile?.freelance}</p>
                  </div>
                  <div className="flex flex-row items-start gap-3 text-sm font-thin">
                    <CiLocationOn size={16} />
                    <p>{profile?.location}</p>
                  </div>
                  <Link
                    to={`tel:${profile?.number}`}
                    className="flex flex-row items-start gap-3 text-sm font-thin w-fit"
                  >
                    <HiOutlinePhone size={16} />
                    <p>{profile?.number}</p>
                    <RiExternalLinkLine size={12} />
                  </Link>
                  <Link
                    to={`mailto:${profile.email}`}
                    className="flex flex-row items-start gap-3 text-sm font-thin w-fit"
                  >
                    <MdOutlineMail size={16} />
                    <p>{profile?.email}</p>
                    <RiExternalLinkLine size={12} />
                  </Link>
                  <Link
                    to={`https://www.${profile?.website}`}
                    target="_blank"
                    className="flex flex-row items-start gap-3 text-sm font-thin w-fit"
                  >
                    <GoLink size={16} />
                    <div className="flex flex-row items-center gap-2">
                      <p>{profile?.website}</p>
                      <CgInternal size={12} />
                    </div>
                  </Link>
                </div>
                <div>
                  <p
                    className={`mb-2 text-xl font-semibold font-saira ${siteTheme.textColor}`}
                  >
                    Education
                  </p>
                  <div className="flex flex-col w-full gap-[5px] font-saira">
                    {school?.map((sch, index) => (
                      <div
                        key={index}
                        className="flex flex-row items-start gap-3 mb-1 text-sm font-thin"
                      >
                        <div className="relative">
                          <GiGraduateCap size={16} />
                          {school?.length !== index + 1 && (
                            <div
                              className={`${
                                isDay ? "bg-neutral-500" : "bg-neutral-700"
                              } w-[1px] h-[35px] absolute top-[16px] right-[50%]`}
                            />
                          )}
                        </div>
                        <div>
                          <p>
                            {sch?.name} - {sch?.location}
                          </p>
                          <p>{sch?.degree}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            className={`${
              isDay
                ? `${siteTheme.textColor} border-neutral-400`
                : "text-neutral-400 border-neutral-800"
            } ${
              isDay ? "border-neutral-400" : "border-neutral-800"
            } border-b-[.5px] w-full mx-auto max-w-max-profile-content-width font-saira font-thin mt-10 pb-16 px-2 md:px-5 lg:px-0`}
          >
            <div
              className={`text-xl mb-10 font-semibold ${siteTheme.textColor}`}
            >
              <p>Experiences</p>
            </div>
            <div className="flex flex-col gap-12">
              {experience?.map((exp, idx) => (
                <Accordion
                  key={idx}
                  data={exp}
                  siteTheme={siteTheme}
                  isDay={isDay}
                />
              ))}
            </div>
          </div>
          <div
            className={`${
              isDay ? siteTheme.textColor : "text-neutral-400"
            } font-saira font-thin duration-300 ease-in-out max-w-max-profile-content-width mx-auto m-2 mb-0 pt-5 px-2 md:px-5 lg:px-0`}
          >
            <div
              className={`text-xl pb-10 font-semibold ${siteTheme.textColor}`}
            >
              <p>Certifications</p>
            </div>
            <div className="gap-2 columns-1 md:columns-3">
              {education.map((edu, idx) => (
                <div
                  key={idx}
                  className={`${siteTheme.borderColor} border-[0.5px] p-2 rounded-lg gap-5 flex flex-row items-center mb-2 w-full break-inside-avoid-column duration-300 ease-in-out`}
                >
                  <div>
                    <IoMdCheckmarkCircleOutline size={20} />
                  </div>
                  <div>
                    <p className="text-[14px] font-semibold">{edu?.title}</p>
                    <p className="text-[10px] tracking-widest">
                      <span className="mr-1">@{edu?.school}</span> |
                      <span className="ml-1">{edu?.date}</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-col items-center justify-center w-full mx-auto my-20 max-w-max-profile-content-width">
            <div className="flex items-center justify-center w-full mx-auto">
              <div className="w-[50%] h-[1px] bg-gradient-to-r from-transparent to-linesLight" />
              <GoDot
                size={20}
                color="rgba(117, 117, 117, 0.5)"
                className="mx-2"
              />
              <div className="w-[50%] h-[1px] bg-gradient-to-r to-transparent from-linesLight" />
            </div>
          </div>
          <TechStackRightSummary
            profileSummaries={profileSummaries}
            siteTheme={siteTheme}
            isDay={isDay}
            techStacks={techStacks}
          />
          <TechStackLeftSummary
            profileSummaries={profileSummaries}
            siteTheme={siteTheme}
            isDay={isDay}
            techStacks={techStacks}
          />
          <div className="flex flex-col items-center justify-center w-full px-10 mx-auto mb-10 sm:mb-20 md:mb-32 max-w-max-profile-content-width lg:px-0">
            <TechStackGroup
              data={techStacks.slice(20, techStacks.length)}
              widthAndheight="w-14 h-14"
              gap="gap-2 lg:gap-4"
            />
          </div>
          <AIComponent
            siteTheme={siteTheme}
            isDay={isDay}
            aiTechStacks={aiTechStacks}
            aiData={aiData}
          />
          <MicroFrontEnd
            state={state}
            mfe={mfe}
            siteTheme={siteTheme}
            isDay={isDay}
          />
          <Achievements
            achievements={achievements}
            siteTheme={siteTheme}
            isDay={isDay}
          />
          <FloatLinks otherInfo={otherInfo} siteTheme={siteTheme} />
        </div>
      </Layout>
    </>
  );
}

export default Profile;
